import { SolButton, SolButtonType, SolEmptyState, SolText } from '../../../../public-api';
import { ObjectTools } from '../../../tools/object-tools';
import { SolRouteId } from '../../../models/sol-navigation';
import { SolErrorPageType } from './sol-error-page.component';

export class State {
  private constructor(
    public readonly type: SolErrorPageType,
    public readonly callbackUri: string | undefined,
    public readonly username: string | undefined,
    public readonly authenticationEnabled: boolean,
    public readonly appRouteId: SolRouteId | undefined,
    public readonly emptyState: SolEmptyState,
    public readonly messages: SolText[],
    public readonly showUsername: boolean,
    public readonly hostClass: string,
  ) {}

  public static of(): State {
    return new State(
      SolErrorPageType.UNKNOWN,
      undefined,
      undefined,
      false,
      undefined,
      State.toEmptyState(SolErrorPageType.UNKNOWN, false, undefined),
      State.toMessages(SolErrorPageType.UNKNOWN),
      false,
      'sol-error-page',
    );
  }

  public withResolved(type: SolErrorPageType, callbackUri: string | undefined, authenticationEnabled: boolean): State {
    if (
      type === this.type &&
      callbackUri === this.callbackUri &&
      authenticationEnabled === this.authenticationEnabled
    ) {
      return this;
    }
    return this.copy({
      type,
      callbackUri,
      authenticationEnabled,
      messages: State.toMessages(type),
      showUsername: type === SolErrorPageType.FORBIDDEN,
    }).recalculateEmptyState();
  }

  public withAppRouteId(appRouteId: SolRouteId | undefined): State {
    if (appRouteId === this.appRouteId) {
      return this;
    }
    return this.copy({ appRouteId }).recalculateEmptyState();
  }

  public withUsername(username: string | undefined): State {
    if (username === this.username) {
      return this;
    }
    return this.copy({ username });
  }

  private recalculateEmptyState(): State {
    return this.copy({ emptyState: State.toEmptyState(this.type, this.authenticationEnabled, this.appRouteId) });
  }

  private copy(props: Partial<State>): State {
    return ObjectTools.copy<State>(this, props);
  }

  private static toEmptyState(
    type: SolErrorPageType,
    authenticationEnabled: boolean,
    appRouteId: SolRouteId | undefined,
  ): SolEmptyState {
    const title = State.toTitle(type);
    const actionButton = State.toActionButton(type, authenticationEnabled, appRouteId);
    return SolEmptyState.of(title).withActionButton(actionButton).withCustomContent(true);
  }

  private static toMessages(type: SolErrorPageType): SolText[] {
    switch (type) {
      case SolErrorPageType.NOT_FOUND:
        return [SolText.of('sol-error-page.not-found.message1'), SolText.of('sol-error-page.not-found.message2')];
      case SolErrorPageType.CONNECTION_ERROR:
        return [
          SolText.of('sol-error-page.connection-error.message1'),
          SolText.of('sol-error-page.connection-error.message2'),
        ];
      case SolErrorPageType.FORBIDDEN:
        return [SolText.of('sol-error-page.forbidden.message1'), SolText.of('sol-error-page.forbidden.message2')];
      default:
        return [SolText.of('sol-error-page.unknown.message1')];
    }
  }

  private static toTitle(type: SolErrorPageType): SolText {
    switch (type) {
      case SolErrorPageType.NOT_FOUND:
        return SolText.of('sol-error-page.not-found.title');
      case SolErrorPageType.CONNECTION_ERROR:
        return SolText.of('sol-error-page.connection-error.title');
      case SolErrorPageType.FORBIDDEN:
        return SolText.of('sol-error-page.forbidden.title');
      default:
        return SolText.of('sol-error-page.unknown.title');
    }
  }

  private static toActionButton(
    type: SolErrorPageType,
    authenticationEnabled: boolean,
    appRouteId: SolRouteId | undefined,
  ): SolButton | undefined {
    switch (type) {
      case SolErrorPageType.NOT_FOUND:
        return SolButton.of('sol-error-page.not-found.button-text').withType(SolButtonType.link);
      case SolErrorPageType.CONNECTION_ERROR:
        return SolButton.of('sol-error-page.connection-error.button-text').withType(SolButtonType.link);
      case SolErrorPageType.FORBIDDEN:
        if (appRouteId !== undefined) {
          return SolButton.of('sol-error-page.forbidden.dashboard-button-text').withType(SolButtonType.link);
        }
        return authenticationEnabled
          ? SolButton.of('sol-error-page.forbidden.button-text').withType(SolButtonType.link)
          : undefined;
      default:
        return undefined;
    }
  }
}
